import { useEffect, useState } from "react"
import type { JSX } from "react"

import { useBrazo } from "../brazo.ts"
import { CeldaFechaHora } from "../celda-fecha-hora.tsx"
import { IdentificadorAcortado } from "../identificador-acortado.tsx"
import { Paginado } from "../paginado.tsx"
import { selloDelBrazo } from "../zonas.ts"
import { textoOGuion } from "./cable.ts"
import type { Atencion, Caso, Conversacion } from "./tipos.ts"

/** El texto de ayuda del panel, palabra por palabra (CA-52). */
export const TEXTO_DE_AYUDA_DEL_PANEL =
  "Todos los mensajes de esta conversación, como en Conversaciones: del más nuevo al más antiguo, en hora de Chile. Solo lectura. La fila marcada es el mensaje que abrió el caso."

/** El aviso cuando el pedido de la conversación no llega (CA-74). */
const AVISO_SIN_SERVIDOR = "No se pudo hablar con el servidor."

/**
 * El panel «Ver conversación» (CA-51 a CA-60, CA-61/62/63, CA-64/65, CA-69,
 * CA-72, CA-74).
 *
 * Por qué es de SOLO LECTURA (CA-65): el panel muestra la conversación tal como
 * quedó; la única acción es «Marcar como atendido», que vive fuera de la tabla.
 * Las filas del cuerpo no llevan botones.
 *
 * Por qué la página viene del servidor (CA-60): la vista no busca la fila
 * marcada ni calcula en qué página cae; el servidor manda `pagina`, `total` y
 * `filaResaltada`.
 */
export function PanelDeConversacion(props: {
  readonly caso: Caso
  readonly conversacion: Conversacion | null
  readonly error: string | null
  readonly tamanoDePagina: number
  readonly alCerrar: () => void
  readonly onPagina: (pagina: number) => void
  readonly onTamanoDePagina: (tamano: number) => void
  readonly onMarcar: (id: string) => Promise<Atencion>
}): JSX.Element {
  const { caso } = props
  const brazo = useBrazo()
  const [atencion, setAtencion] = useState<Atencion>(caso.atencion)
  const [marcando, setMarcando] = useState(false)
  const [errorDeMarca, setErrorDeMarca] = useState<string | null>(null)

  useEffect(() => {
    function alTeclear(evento: KeyboardEvent): void {
      if (evento.key === "Escape") props.alCerrar()
    }
    window.addEventListener("keydown", alTeclear)
    return () => window.removeEventListener("keydown", alTeclear)
  }, [props])

  async function alMarcar(): Promise<void> {
    setMarcando(true)
    setErrorDeMarca(null)
    try {
      // El estado de atención se pinta con lo que DEVUELVE el servidor: si la
      // marca falla, el caso sigue sin atender (CA-72/D8).
      const actualizada = await props.onMarcar(caso.id)
      setAtencion(actualizada)
    } catch (falla) {
      setErrorDeMarca("No se pudo marcar el caso. Intenta de nuevo.")
    } finally {
      setMarcando(false)
    }
  }

  const atendido = atencion.estado === "atendido"
  const estadoDelCaso = atendido ? "Atendido" : "Sin atender"
  const aviso = props.error === null ? null : props.error

  return (
    <>
      <div className="situaciones-criticas__panel-fondo" onClick={props.alCerrar} />
      <section className="situaciones-criticas__panel" role="dialog" aria-modal="true">
        {/*
          La MIGA del panel (PRD:900): «Situaciones críticas · <instancia> ·
          Brazo X». ⛔ El brazo y la instancia se INTERPOLAN con `selloDelBrazo`,
          nunca se escriben literales: el guard F4 RT 1 del spec prohíbe el nombre
          de la instancia escrito a mano (en UAT el sello lleva «(ambiente de
          UAT)» y el literal mentiría). Sin brazo declarado la miga es sólo
          «Situaciones críticas»: no se inventa instancia. Va FUERA del `<header>`
          —que es flex y sin `wrap`— para que caiga en su propio renglón sin
          apretar el título ni «Cerrar».
        */}
        <p className="situaciones-criticas__panel-miga">
          {brazo === null ? "Situaciones críticas" : `Situaciones críticas · ${selloDelBrazo(brazo)}`}
        </p>
        <header className="situaciones-criticas__panel-encabezado">
          <h2 className="situaciones-criticas__panel-titulo">
            Conversación
            {caso.id_conversacion !== null && (
              <>
                {" "}
                <IdentificadorAcortado identificador={caso.id_conversacion} que="Conversación" />
              </>
            )}
          </h2>
          <button type="button" onClick={props.alCerrar}>
            Cerrar
          </button>
        </header>
        <p className="situaciones-criticas__panel-linea">
          <span className="situaciones-criticas__panel-nombre">{caso.participante?.nombre ?? "—"}</span> ·{" "}
          <span className="situaciones-criticas__panel-telefono">
            {textoOGuion(caso.participante?.telefono)}
          </span>{" "}
          ·{" "}
          <span className="situaciones-criticas__panel-lote">{textoOGuion(caso.participante?.lote)}</span>{" "}
          · <span className="situaciones-criticas__panel-categoria">{caso.categoria}</span> ·{" "}
          <span className="situaciones-criticas__panel-estado-del-caso">{estadoDelCaso}</span>
        </p>
        <p className="situaciones-criticas__panel-ayuda">{TEXTO_DE_AYUDA_DEL_PANEL}</p>
        {!atendido && (
          <button type="button" disabled={marcando} onClick={alMarcar}>
            Marcar como atendido
          </button>
        )}
        {errorDeMarca !== null && (
          <p className="situaciones-criticas__error-de-marca">{errorDeMarca}</p>
        )}
        {props.conversacion === null && aviso === null ? (
          <p className="situaciones-criticas__panel-cargando">Cargando…</p>
        ) : (
          <table className="situaciones-criticas__tabla">
            <thead>
              <tr>
                <th>Fecha y hora</th>
                <th>¿Del sistema?</th>
                <th>Mensaje</th>
                <th>Estado</th>
              </tr>
            </thead>
            <tbody>
              {props.conversacion === null ? (
                // CA-74 · con el pedido caído la TABLA queda con el aviso y nada más.
                <tr>
                  <td colSpan={4} className="situaciones-criticas__panel-error">
                    {AVISO_SIN_SERVIDOR}
                  </td>
                </tr>
              ) : props.conversacion.mensajes.map((mensaje) => (
                <tr
                  key={mensaje.id}
                  className={
                    props.conversacion !== null && props.conversacion.filaResaltada === mensaje.id
                      ? "situaciones-criticas__panel-fila-resaltada"
                      : undefined
                  }
                >
                  <td className="situaciones-criticas__panel-celda-fecha">
                    <CeldaFechaHora fecha={mensaje.fecha} />
                  </td>
                  <td className="situaciones-criticas__panel-celda-sistema">
                    {/*
                      PRD:904 · «sí» o «no», en MINÚSCULA: es una etiqueta corta
                      dentro de la celda y no el inicio de una oración. La misma
                      regla vive en `conversaciones/formato.ts:91-92`
                      (`formatearEsSistema`), pero esta pantalla NO puede
                      importar de `conversaciones/**` (`territories.yaml:95-99`,
                      guard del spec): por eso se escribe acá, no se reusa.
                    */}
                    {mensaje.delSistema ? "sí" : "no"}
                  </td>
                  <td className="situaciones-criticas__panel-celda-mensaje">
                    <span>{mensaje.texto}</span>
                    {mensaje.lineasDeAyuda.length > 0 &&
                      mensaje.lineasDeAyuda.map((linea) => (
                        <p key={linea} className="situaciones-criticas__panel-linea-ayuda">
                          {linea}
                        </p>
                      ))}
                  </td>
                  <td className="situaciones-criticas__panel-celda-estado">
                    <span>{mensaje.estado}</span>
                    {mensaje.motivo === null ? null : (
                      <p className="situaciones-criticas__panel-motivo">{mensaje.motivo}</p>
                    )}
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        )}
        {props.conversacion !== null && (
          <Paginado
            etiqueta="mensajes"
            pagina={props.conversacion.pagina}
            total={props.conversacion.total}
            tamanoDePagina={props.tamanoDePagina}
            unidad={{ singular: "mensaje", plural: "mensajes" }}
            onPagina={props.onPagina}
            onTamanoDePagina={props.onTamanoDePagina}
          />
        )}
      </section>
    </>
  )
}
